import React from 'react';
import { Await, useLoaderData, useNavigate, useSubmit } from 'react-router-dom';

import { DataTable } from '@oysterjs/ui/data-table';
import { SearchFilterInput, useFiltersFromLocalStorage } from '@oysterjs/ui/search-filter';

import {
  Action,
  Resource,
  UpdateWordDocumentTemplateMutation,
  VisibilityConfigInput,
  WordDocumentTemplateFieldsFragment
} from '~/api/graphql';
import { PermissionButton, usePermissions } from '~/components/permissions';
import { BulkVisibilityDialog } from '~/components/visibility/bulk-visibility-dialog';
import { useVisibilityEntitlement } from '~/components/visibility/use-visibility-entitlement';

import { ArchiveConfirmDialog } from '~/components/list/archive-confirm-dialog';
import { FeatureDocCallout } from '~/components/library/feature-doc-callout';
import { FloatingSelectionBar } from '~/components/list/floating-selection-bar';
import { LibraryEditOverlayLoader } from '~/components/library/library-edit-overlay-loader';
import { LibraryListEmptyState } from '~/components/list/library-list-empty-state';
import { LibraryListLoading } from '~/components/list/library-list-loading';
import { LibraryListOutlet } from '~/components/library/library-list-outlet';
import { WordTemplateEditor } from '~/components/library/word-template-editor';
import { WordTemplateCreateDialog } from '~/components/library/word-template-create-dialog';
import { duplicateWordTemplateFormState } from '~/components/library/lib/word-template-duplicate-form';
import type { WordTemplateFormState } from '~/components/library/lib/word-template-sections';
import {
  getWordTemplateColumns,
  wordTemplateRowHref,
  wordTemplateSortFn,
  type WordTemplateSortKey
} from '~/components/library/word-template-columns';
import {
  createStyleFilterDefinitions,
  filterStyleTemplates,
  type StyleFilter
} from '~/api/data/html-templates/styles-filters';
import { useActionValidationErrors } from '~/components/library/hooks/use-action-validation-errors';
import { useCurrentViewer } from '~/components/library/hooks/use-current-viewer';
import { useFloatingSelection } from '~/components/list/use-floating-selection-bar';
import { useLibraryBulkArchive } from '~/components/list/use-library-bulk-archive';
import { useLibraryBulkVisibility } from '~/components/library/hooks/use-library-bulk-visibility';

const ARCHIVE_PATH = '/app/library/word-templates/archive';
const VISIBILITY_PATH = '/app/library/word-templates/visibility';

const CREATE_WORD_TEMPLATE = [{ resource: Resource.WordDocumentTemplate, action: Action.Create }];
const UPDATE_WORD_TEMPLATE = [{ resource: Resource.WordDocumentTemplate, action: Action.Update }];

/**
 * Word templates tab of the library. Lists uploaded .docx templates with
 * Office Online preview on edit. Bulk actions go through router actions so
 * the parent loader revalidates automatically.
 */
export const LibraryWordTemplatesPage = () => {
  const loader = useLoaderData() as {
    data?: Promise<{ wordDocumentTemplates?: WordDocumentTemplateFieldsFragment[] }>;
  };

  const viewer = useCurrentViewer();
  const visibilityEntitled = useVisibilityEntitlement();
  const canUpdate = usePermissions().hasAllPermissions(UPDATE_WORD_TEMPLATE);

  const [uploadDialogOpen, setUploadDialogOpen] = React.useState(false);

  const filterDefinitions = React.useMemo(() => createStyleFilterDefinitions(), []);
  const { activeFilters, setActiveFilters, initialFilters, sortBy, sortDirection, setSort } =
    useFiltersFromLocalStorage<StyleFilter, WordTemplateSortKey>(
      'cara_word_templates_default_view',
      filterDefinitions,
      { defaultSortDirection: 'desc' }
    );

  const { selected, setSelected, selectedSet, clear } = useFloatingSelection();

  const archive = useLibraryBulkArchive({
    actionPath: ARCHIVE_PATH,
    onArchived: clear
  });

  const visibility = useLibraryBulkVisibility({
    actionPath: VISIBILITY_PATH,
    itemPlural: 'word templates',
    onUpdated: clear
  });

  return (
    <div className="space-y-4">
      <FeatureDocCallout
        variant="sky"
        title="Word templates"
        description="Upload .docx templates with placeholders. Cara fills them in to generate Word documents directly from a workspace."
        docHref="https://docs.getcara.ai"
      />
      <div className="flex flex-wrap items-start gap-4">
        <div className="min-w-0 flex-1">
          <SearchFilterInput
            filterDefinitions={filterDefinitions}
            onFiltersChange={setActiveFilters}
            initialFilters={initialFilters}
            placeholder="Filter by title, creator, or search..."
          />
        </div>
        <PermissionButton
          permissions={CREATE_WORD_TEMPLATE}
          whenDenied="hide"
          color="sky"
          type="button"
          className="min-h-12"
          onClick={() => setUploadDialogOpen(true)}
        >
          Upload word template
        </PermissionButton>
      </div>
      <React.Suspense fallback={<LibraryListLoading title="Loading word templates..." />}>
        <Await resolve={loader?.data}>
          {(data: { wordDocumentTemplates?: WordDocumentTemplateFieldsFragment[] } | undefined) => {
            const visible = filterStyleTemplates(
              data?.wordDocumentTemplates ?? [],
              activeFilters,
              viewer
            );
            return (
              <div className="relative">
                {canUpdate && (
                  <FloatingSelectionBar
                    count={selected.length}
                    onClear={clear}
                    onVisibility={() => visibility.openDialog(selected)}
                    onArchive={archive.openDialog}
                    isArchiving={archive.isArchiving}
                    isUpdatingVisibility={visibility.isUpdating}
                    visibilityEnabled={visibilityEntitled}
                  />
                )}
                {visible.length === 0 ? (
                  <LibraryListEmptyState
                    title="No word templates found"
                    description={
                      activeFilters.length > 0
                        ? 'Try adjusting your search or filters.'
                        : 'No word templates available. Upload a .docx file to get started.'
                    }
                  />
                ) : (
                  <div className="px-4 sm:px-6 rounded-lg bg-white shadow dark:bg-neutral-800">
                    <DataTable
                      data={visible}
                      columns={getWordTemplateColumns({
                        selectedSet,
                        setSelectedWordTemplates: setSelected,
                        filtered: visible,
                        canEdit: canUpdate
                      })}
                      keyExtractor={(template) => template.id}
                      rowHref={wordTemplateRowHref}
                      tableClassName="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
                      sortKey={sortBy}
                      sortDirection={sortDirection}
                      onSort={setSort}
                      sortFn={wordTemplateSortFn}
                    />
                  </div>
                )}
              </div>
            );
          }}
        </Await>
      </React.Suspense>

      <ArchiveConfirmDialog
        open={archive.dialogOpen}
        count={selected.length}
        itemNoun="word template"
        itemPlural="word templates"
        loading={archive.isArchiving}
        onCancel={archive.closeDialog}
        onConfirm={() => archive.archive(selected)}
      />

      <BulkVisibilityDialog
        open={visibility.open}
        count={visibility.ids.length}
        itemLabel="word template"
        notice="This changes only the selected word templates. Existing documents keep their current visibility."
        resultMessage={visibility.resultMessage}
        loading={visibility.isUpdating}
        onClose={visibility.closeDialog}
        onSave={(v: VisibilityConfigInput) => visibility.save(v)}
      />

      <WordTemplateCreateDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        action="/app/library/word-templates/new"
      />

      <LibraryListOutlet />
    </div>
  );
};

/** Edit slideout for a single word template; body is a DOCX preview. */
export const WordTemplateEditPage = () => {
  const loader = useLoaderData() as
    | { data: Promise<{ wordDocumentTemplate?: WordDocumentTemplateFieldsFragment | null }> }
    | undefined;
  const submit = useSubmit();
  const navigate = useNavigate();
  const validationErrors = useActionValidationErrors<UpdateWordDocumentTemplateMutation>();
  const listUrl = '/app/library/word-templates';
  const [duplicateDialogOpen, setDuplicateDialogOpen] = React.useState(false);
  const [duplicateInitialForm, setDuplicateInitialForm] =
    React.useState<WordTemplateFormState | null>(null);
  const [duplicateSectioned, setDuplicateSectioned] = React.useState(true);

  // The editor builds the entire `multipart/form-data` payload (title,
  // description, fill guidance, visibility, sections JSON + per-index files);
  // we just hand it off to the router action.
  const submitForm = React.useCallback(
    (formData: FormData) => {
      submit(formData, { method: 'PUT', encType: 'multipart/form-data' });
    },
    [submit]
  );

  return (
    <React.Suspense fallback={<LibraryEditOverlayLoader />}>
      <Await resolve={loader?.data}>
        {(
          data: { wordDocumentTemplate?: WordDocumentTemplateFieldsFragment | null } | undefined
        ) => {
          if (!data?.wordDocumentTemplate) return null;
          const template = data.wordDocumentTemplate;
          return (
            <>
              <WordTemplateEditor
                wordTemplate={template}
                validationErrors={validationErrors}
                listUrl={listUrl}
                onSubmit={submitForm}
                onDuplicate={() => {
                  const { form, sectioned } = duplicateWordTemplateFormState(template);
                  setDuplicateInitialForm(form);
                  setDuplicateSectioned(sectioned);
                  setDuplicateDialogOpen(true);
                }}
              />
              <WordTemplateCreateDialog
                open={duplicateDialogOpen}
                mode="duplicate"
                initialForm={duplicateInitialForm}
                initialSectioned={duplicateSectioned}
                onSuccess={() => navigate(listUrl)}
                onClose={() => {
                  setDuplicateDialogOpen(false);
                  setDuplicateInitialForm(null);
                }}
                action="/app/library/word-templates/new"
              />
            </>
          );
        }}
      </Await>
    </React.Suspense>
  );
};
